import { Link } from "@tanstack/react-router";
import { ArrowRight, Share2, Trophy } from "lucide-react";
import { useState } from "react";
import { track } from "@/lib/analytics";
import { buildShareGrid, challengeUrl, shareGrid, tierFor } from "@/lib/share-grid";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

/**
 * Status banner - turns the reader's local "call it" record into a
 * shareable badge. Numbers are real (from localStorage), never invented.
 */
export function CallRecordBanner({
  correct,
  total,
  streak,
  bestStreak = 0,
  recent = [],
  url,
}: {
  correct: number;
  total: number;
  streak: number;
  bestStreak?: number;
  recent?: boolean[];
  url: string;
}) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const [copied, setCopied] = useState(false);
  if (total < 1) return null;

  const headline =
    streak >= 2
      ? `${streak} impossible stories called correctly in a row.`
      : correct >= 1
        ? `You called it. ${correct} of ${total} so far.`
        : `You didn't buy it - and it was still true. ${total} down.`;

  const share = async () => {
    track("Call Record Share", { streak, total });
    const text = buildShareGrid({ recent, streak, bestStreak, correct, total });
    const slug = url.split("?")[0].split("/").filter(Boolean).pop() ?? null;
    const res = await shareGrid(text, challengeUrl(slug, streak));
    if (res === "copied") {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2400);
    }
  };

  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-5 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <Trophy className="h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">{ui("Your record")}</div>
          <div className="mt-0.5 font-semibold text-foreground">{headline}</div>
          {recent.length ? (
            <div className="mt-1 text-sm tracking-[0.15em]" aria-hidden>
              {recent.slice(-5).map((r) => (r ? "🟩" : "🟥")).join("")}
              <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {tierFor(bestStreak)}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        onClick={share}
        className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-primary-foreground transition hover:brightness-110"
      >
        <Share2 className="h-3.5 w-3.5" /> {ui(copied ? "Copied" : "Challenge a friend")}
      </button>
    </div>
  );
}

/**
 * Cliffhanger portal - the next discovery, without giving away the title.
 * Curiosity gap first, payoff after the tap.
 */
export function NextPortal({
  slug,
  teaser,
  fallbackTeaser,
}: {
  slug: string;
  teaser: string | null;
  fallbackTeaser: string;
}) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const line = (teaser ?? "").trim().split(/(?<=[.!?])\s/)[0] || fallbackTeaser;
  const trimmed = line.length > 120 ? `${line.slice(0, 117).trimEnd()}…` : line;
  return (
    <Link
      to="/discovery/$slug"
      params={{ slug }}
      onClick={() => track("Next Portal Click", { slug })}
      className="mt-6 block overflow-hidden rounded-3xl border border-primary/50 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-5 transition hover:border-primary hover:from-primary/25 md:p-7"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">{ui("Next discovery")}</div>
      <p className="mt-2 font-display text-lg uppercase leading-tight text-foreground md:text-2xl">{trimmed}</p>
      <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-primary">
        {ui("Reveal it")} <ArrowRight className="h-4 w-4" />
      </span>
    </Link>
  );
}
